import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { TrendingUp, Percent, Shield, PieChart, BarChart3, Briefcase, Building, FileText, Scale } from 'lucide-react';

/* ═══════════════════════════════════════════
   EDITABLE: Products data with investor insights
═══════════════════════════════════════════ */
const products = [
  {
    icon: TrendingUp,
    title: 'Mutual Funds',
    description: 'A range of fund options to help you plan and progress toward your future goals.',
    insight: 'Beyond basic SIPs and lump-sum investments, Mutual Funds provide a gateway to professional fund management and tax-efficient wealth creation. By diversifying across sectors, investors can mitigate idiosyncratic risk while benefiting from the power of compounding and rupee-cost averaging in volatile markets.',
  },
  {
    icon: Percent,
    title: 'Fixed Income',
    description: 'Structured options that aim to deliver steady outcomes with minimal fluctuation.',
    insight: 'These instruments are the bedrock of capital preservation. Designed for investors seeking predictable cash flows with minimal volatility, our fixed-income strategies focus on high-quality debt instruments that aim to beat inflation while providing a safety net against equity market downturns.',
  },
  {
    icon: Shield,
    title: 'Insurance',
    description: 'Complete coverage solutions to safeguard your life, health, and long-term priorities.',
    insight: 'True insurance is a strategic risk-mitigation tool, not just an expense. We focus on comprehensive life and health solutions that act as a financial moat, ensuring your long-term wealth goals and family\'s lifestyle remain protected against life\'s unpredictable "black swan" events.',
  },
  {
    icon: PieChart,
    title: 'Model Portfolio',
    description: 'Ready-made combinations designed for different needs and timelines.',
    insight: 'These are algorithmically back-tested or expert-curated baskets of assets designed for specific risk profiles. Instead of managing individual stocks, investors get a balanced, periodically rebalanced portfolio that aligns perfectly with their specific financial horizon and return expectations.',
  },
  {
    icon: BarChart3,
    title: 'Direct Equity & ETFs',
    description: 'Options to participate in listed markets through equity and fund-based instruments.',
    insight: 'For those who want transparency and direct ownership, these options offer the highest growth potential. ETFs provide low-cost, broad market exposure, while direct equity allows for surgical precision in sector participation, backed by rigorous fundamental analysis to identify undervalued opportunities.',
  },
  {
    icon: Briefcase,
    title: 'PMS',
    description: 'Specialized portfolio handling for individuals with larger capital and personalized planning.',
    insight: 'Tailored for High Net-Worth Individuals (HNIs), PMS offers a concentrated, professional approach to wealth management. Investors benefit from direct communication with portfolio managers, customized investment mandates, and a level of portfolio agility that is often unavailable in standard mutual funds.',
  },
  {
    icon: Building,
    title: 'AIF',
    description: 'Access exclusive market-linked opportunities that go beyond conventional instruments.',
    insight: 'These are sophisticated vehicles for investors looking to move beyond the traditional stock and bond markets. From private equity and hedge funds to distressed assets, AIFs offer a path to higher alpha and low correlation with the broader market, suitable for a diversified, mature portfolio.',
  },
  {
    icon: FileText,
    title: 'LAS',
    description: 'Enhance liquidity by using your existing investments as collateral for funding needs.',
    insight: 'Unlock the value of your portfolio without disrupting your investment strategy. By using your holdings as collateral, you can access immediate liquidity for business or personal needs at competitive rates, ensuring your long-term compounding remains uninterrupted.',
  },
  {
    icon: Scale,
    title: 'Succession Planning',
    description: 'Ensure smooth legacy transfer with professionally guided planning and will documentation.',
    insight: 'Wealth preservation is incomplete without a clear transfer strategy. We provide a roadmap for the seamless transition of your legacy, covering Will documentation, private trusts, and family governance to ensure your hard-earned assets are protected and distributed according to your exact wishes.',
  },
];

export const ProductsSection = () => {
  const [activeIndex, setActiveIndex] = useState(0);
  const [mobileExpanded, setMobileExpanded] = useState(null);

  return (
    <section id="products" data-testid="products-section" className="py-20 md:py-32 bg-white">
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-20">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-100px' }}
          transition={{ duration: 0.7 }}
          className="text-center mb-16"
        >
          <p className="text-xs tracking-[0.25em] uppercase font-bold text-[#D4AF37] mb-4">
            Our Offerings
          </p>
          <h2 className="font-['Playfair_Display'] text-3xl sm:text-4xl font-bold text-[#0A192F]">
            Products We Offer
          </h2>
        </motion.div>

        {/* Desktop: 60/40 Grid + Sidebar */}
        <div className="hidden lg:flex gap-8">
          {/* Left Grid - 60% */}
          <div className="w-[60%] grid grid-cols-2 gap-4">
            {products.map((product, index) => (
              <motion.div
                key={product.title}
                data-testid={`product-card-${index}`}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: index * 0.05 }}
                onMouseEnter={() => setActiveIndex(index)}
                className={`group flex items-start gap-3 p-5 rounded-xl cursor-pointer transition-all duration-300 ${
                  activeIndex === index
                    ? 'bg-[#0A192F] shadow-lg'
                    : 'bg-[#F8F9FA] hover:bg-[#0A192F]/5'
                }`}
              >
                <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 transition-colors ${
                  activeIndex === index ? 'bg-[#D4AF37]' : 'bg-white'
                }`}>
                  <product.icon size={18} className="text-[#0A192F]" />
                </div>
                <div>
                  <h4 className={`font-['Playfair_Display'] text-sm font-bold mb-1 transition-colors ${
                    activeIndex === index ? 'text-white' : 'text-[#0A192F]'
                  }`}>
                    {product.title}
                  </h4>
                  <p className={`text-xs leading-relaxed transition-colors font-['Outfit'] ${
                    activeIndex === index ? 'text-white/70' : 'text-[#49566B]'
                  }`}>
                    {product.description}
                  </p>
                </div>
              </motion.div>
            ))}
          </div>

          {/* Right Sidebar - 40% */}
          <div className="w-[40%] sticky top-28 self-start">
            <div className="bg-white/80 backdrop-blur-xl border border-[#0A192F]/10 rounded-2xl p-8 shadow-xl h-[520px] flex flex-col" data-testid="investor-sidebar">
              <p className="text-xs tracking-[0.2em] uppercase font-bold text-[#D4AF37] mb-4">
                Investor Insights
              </p>
              <AnimatePresence mode="wait">
                <motion.div
                  key={activeIndex}
                  initial={{ opacity: 0, y: 15 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  transition={{ duration: 0.35 }}
                  className="flex-1 flex flex-col"
                >
                  <div className="flex items-center gap-3 mb-5">
                    <div className="w-12 h-12 bg-[#0A192F] rounded-xl flex items-center justify-center">
                      {(() => {
                        const Icon = products[activeIndex].icon;
                        return <Icon size={22} className="text-[#D4AF37]" />;
                      })()}
                    </div>
                    <h3 className="font-['Playfair_Display'] text-xl font-bold text-[#0A192F]">
                      {products[activeIndex].title}
                    </h3>
                  </div>
                  <p className="text-sm text-[#49566B] leading-relaxed font-['Outfit'] flex-1">
                    {products[activeIndex].insight}
                  </p>
                  <a
                    href="#contact"
                    data-testid="sidebar-contact-btn"
                    className="mt-5 inline-block px-6 py-2.5 bg-[#0A192F] text-[#D4AF37] text-sm font-semibold rounded-full hover:scale-105 transition-all text-center border border-[#D4AF37]/30"
                  >
                    Get Started
                  </a>
                </motion.div>
              </AnimatePresence>
            </div>
          </div>
        </div>

        {/* Mobile: Expandable Cards */}
        <div className="lg:hidden space-y-3">
          {products.map((product, index) => (
            <motion.div
              key={product.title}
              data-testid={`product-card-mobile-${index}`}
              initial={{ opacity: 0, y: 15 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.3, delay: index * 0.05 }}
              className="bg-[#F8F9FA] rounded-xl overflow-hidden"
            >
              <button
                onClick={() => setMobileExpanded(mobileExpanded === index ? null : index)}
                className="w-full flex items-center gap-3 p-5 text-left"
              >
                <div className="w-10 h-10 bg-white rounded-lg flex items-center justify-center flex-shrink-0">
                  <product.icon size={18} className="text-[#0A192F]" />
                </div>
                <div className="flex-1">
                  <h4 className="font-['Playfair_Display'] text-sm font-bold text-[#0A192F]">
                    {product.title}
                  </h4>
                  <p className="text-xs text-[#49566B] font-['Outfit']">{product.description}</p>
                </div>
              </button>
              {mobileExpanded === index && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: 'auto', opacity: 1 }}
                  transition={{ duration: 0.3 }}
                  className="px-5 pb-5"
                >
                  <div className="bg-white/80 backdrop-blur-md rounded-xl p-5 border border-[#0A192F]/10">
                    <p className="text-xs tracking-[0.15em] uppercase font-bold text-[#D4AF37] mb-2">Investor Insight</p>
                    <p className="text-sm text-[#49566B] leading-relaxed font-['Outfit']">
                      {product.insight}
                    </p>
                  </div>
                </motion.div>
              )}
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};
